import React, { useState } from 'react';
import { Box, IconButton, Typography, Divider, Snackbar, Alert } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import ChatIcon from '@mui/icons-material/Chat'; // Messenger için
import { NewsArticle } from '../types';

interface SocialShareButtonsProps {
  article: NewsArticle;
  currentUrl: string;
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({ article, currentUrl }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Cihaz türü ve platform algılama
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  // Paylaşım URL'lerini encode edelim
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(article.title);
  const encodedDescription = encodeURIComponent(article.summary || article.title);

  // Paylaşım mesajı
  const shareText = `${article.title}\n\n${currentUrl}`;
  const encodedShareText = encodeURIComponent(shareText);

  // Facebook paylaşım URL'si
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`;

  // X (Twitter) paylaşım URL'si
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&via=akademikadro`;

  // WhatsApp paylaşım URL'si
  const whatsappUrl = `https://wa.me/?text=${encodedShareText}`;
  const whatsappWebUrl = `https://web.whatsapp.com/send?text=${encodedShareText}`;

  // Messenger paylaşım URL'si
  const messengerUrl = `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=YOUR_APP_ID&redirect_uri=${encodedUrl}`;

  // Telegram paylaşım URL'si
  const telegramUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`;

  // Instagram story paylaşımı için gelişmiş fonksiyon
  const handleInstagramShare = async () => {
    if (isMobile) {
      try {
        // Instagram Universal Link deneme (mobilde Instagram uygulamasını açar)
        const instagramStoryUrl = 'https://www.instagram.com/create/story';
        
        // URL'i panoya kopyala
        await navigator.clipboard.writeText(currentUrl);
        
        // Instagram story oluşturma sayfasını aç
        if (isIOS) {
          // iOS için Instagram uygulamasını direkt açmaya çalış
          const instagramAppUrl = 'instagram://camera';
          window.location.href = instagramAppUrl;
          
          // Eğer uygulama açılmazsa, web versiyonuna yönlendir
          setTimeout(() => {
            window.open(instagramStoryUrl, '_blank', 'noopener,noreferrer');
          }, 1000);
        } else if (isAndroid) {
          // Android için Intent URL kullan
          const intentUrl = `intent://instagram.com/create/story#Intent;package=com.instagram.android;scheme=https;end`;
          window.location.href = intentUrl;
          
          // Fallback: Web versiyonu
          setTimeout(() => {
            window.open(instagramStoryUrl, '_blank', 'noopener,noreferrer');
          }, 1000);
        } else {
          // Diğer mobil cihazlar için
          window.open(instagramStoryUrl, '_blank', 'noopener,noreferrer');
        }
        
        setSnackbarMessage('📱 Instagram story sayfası açılıyor! Link kopyalandı, story\'nize yapıştırabilirsiniz. 📸');
        setSnackbarOpen(true);
        
      } catch (err) {
        // Fallback: Sadece URL kopyala
        handleInstagramCopyUrl();
      }
    } else {
      // Desktop için sadece URL kopyala
      handleInstagramCopyUrl();
    }
  };

  const handleInstagramCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setSnackbarMessage('📋 Haber linki kopyalandı! Instagram story\'nize yapıştırarak paylaşabilirsiniz. 📸');
      setSnackbarOpen(true);
    } catch (err) {
      // Eski tarayıcılar için fallback
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbarMessage('📋 Haber linki kopyalandı! Instagram story\'nize yapıştırarak paylaşabilirsiniz. 📸');
      setSnackbarOpen(true);
    }
  };

  // WhatsApp paylaşımı için gelişmiş fonksiyon
  const handleWhatsAppShare = () => {
    if (isMobile) {
      // Mobilde direkt WhatsApp uygulamasını aç
      window.location.href = whatsappUrl;
    } else {
      // Desktop'ta WhatsApp Web aç
      window.open(whatsappWebUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = async (platform: string, url: string) => {
    if (platform === 'instagram') {
      await handleInstagramShare();
    } else if (platform === 'whatsapp') {
      await handleWhatsAppShare();
    } else if (platform === 'telegram') {
      // Telegram için link aç
      if (isMobile) {
        window.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else if (platform === 'messenger') {
      // Messenger için basit URL paylaşımı
      const messengerUrlSimple = `https://m.me/?link=${encodedUrl}`;
      window.open(messengerUrlSimple, '_blank', 'noopener,noreferrer');
    } else {
      // Facebook ve Twitter için paylaşım popup'ı
      const popup = window.open(
        url,
        'social-share',
        'width=600,height=400,scrollbars=yes,resizable=yes'
      );

      // Popup engellenirse normal pencerede aç
      if (!popup) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ my: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Bu haberi paylaş:
        </Typography>
        
        {/* Facebook */}
        <IconButton
          onClick={() => handleShare('facebook', facebookUrl)}
          sx={{ 
            color: '#1877F2',
            '&:hover': { 
              backgroundColor: 'rgba(24, 119, 242, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title="Facebook'ta paylaş"
        >
          <FacebookIcon />
        </IconButton>

        {/* X (Twitter) */}
        <IconButton
          onClick={() => handleShare('twitter', twitterUrl)}
          sx={{ 
            color: '#000000',
            '&:hover': { 
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title="X'te paylaş"
        >
          <TwitterIcon />
        </IconButton>

        {/* WhatsApp */}
        <IconButton
          onClick={() => handleShare('whatsapp', '')}
          sx={{ 
            color: '#25D366',
            '&:hover': { 
              backgroundColor: 'rgba(37, 211, 102, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title={isMobile ? "WhatsApp'ta paylaş (Direkt uygulama)" : "WhatsApp Web'de paylaş"}
        >
          <WhatsAppIcon />
        </IconButton>

        {/* Messenger */}
        <IconButton
          onClick={() => handleShare('messenger', messengerUrl)}
          sx={{ 
            color: '#0084FF',
            '&:hover': { 
              backgroundColor: 'rgba(0, 132, 255, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title="Messenger'da paylaş"
        >
          <ChatIcon />
        </IconButton>

        {/* Telegram */}
        <IconButton
          onClick={() => handleShare('telegram', telegramUrl)}
          sx={{ 
            color: '#0088CC',
            '&:hover': { 
              backgroundColor: 'rgba(0, 136, 204, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title="Telegram'da paylaş"
        >
          <TelegramIcon />
        </IconButton>

        {/* Instagram */}
        <IconButton
          onClick={() => handleShare('instagram', '')}
          sx={{ 
            color: '#E4405F',
            '&:hover': { 
              backgroundColor: 'rgba(228, 64, 95, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          title={isMobile ? "Instagram Story'de paylaş (Direkt açar)" : "Instagram Story'de paylaş"}
        >
          <InstagramIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mt: 2 }} />

      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SocialShareButtons; 